/* eslint-disable no-console */
/* eslint-disable react/jsx-props-no-spreading */
import { Form, Row, Select } from "antd";
import { TypeCommon } from "models/common";
import { ReactElement, useEffect, useRef, useState } from "react";
import "./index.css";

const BaseSelect = ({
  name,
  label,
  required = false,
  rule = [],
  className = "",
  options,
  restField,
  disabled = false,
  allowClear = true,
  multiple = false,
  readOnly = false,
  isRequired = false,
  onClear,
  values,
  sortSelectedFirst = false,
}: {
  name: TypeCommon;
  label?: TypeCommon;
  required?: boolean;
  rule?: TypeCommon;
  className?: string;
  options: TypeCommon;
  restField?: TypeCommon;
  disabled?: boolean;
  allowClear?: boolean;
  multiple?: boolean;
  readOnly?: boolean;
  isRequired?: boolean;
  onClear?: TypeCommon;
  values?: TypeCommon;
  sortSelectedFirst?: boolean;
}): ReactElement => {
  const isChanged = useRef<boolean>(false);
  const [listOptions, setListOptions] = useState<TypeCommon>(options);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  useEffect(() => {
    // Only sort when dropdown is open, sortSelectedFirst is enabled, and values exist
    if (isChanged.current) {
      return;
    }
    const shouldSort =
      isDropdownVisible &&
      sortSelectedFirst &&
      ((multiple && Array.isArray(values) && values.length > 0) ||
        (!multiple && values !== undefined && values !== null));

    if (!shouldSort) {
      setListOptions(options);
      return;
    }

    // Helper function to check if a value is selected
    const isValueSelected = (itemValue: TypeCommon): boolean => {
      if (multiple) {
        return (
          Array.isArray(values) &&
          values.length > 0 &&
          values.includes(itemValue)
        );
      }
      return values !== undefined && values !== null && itemValue === values;
    };

    // Sort options: selected items first
    const selected: TypeCommon = [];
    const unselected: TypeCommon = [];

    options?.forEach((item: TypeCommon) => {
      if (isValueSelected(item?.value)) {
        selected.push(item);
      } else {
        unselected.push(item);
      }
    });

    setListOptions([...selected, ...unselected]);
  }, [isDropdownVisible, options]);
  return (
    <Form.Item
      name={name}
      {...restField}
      required={required}
      label={
        <Row className={`${label ? "text-[14px] " : "!hidden"}`}>
          {label}
          {isRequired && <Row className="text-red-500 ml-1">＊</Row>}
        </Row>
      }
      rules={rule}
      className={className}
    >
      {multiple ? (
        <Select
          allowClear={allowClear}
          showSearch
          id={name}
          mode="multiple"
          onClear={onClear}
          onDropdownVisibleChange={(open: TypeCommon) => {
            setIsDropdownVisible(open);
            if (!open) {
              isChanged.current = false;
            }
          }}
          onSelect={() => {
            isChanged.current = true;
          }}
          maxTagCount="responsive"
          optionFilterProp="children"
          filterOption={(input, option): TypeCommon => {
            const value: TypeCommon = option?.children;
            return value?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
          }}
          disabled={disabled}
          removeIcon={readOnly ? false : undefined}
          open={readOnly ? false : undefined}
          className={`${readOnly ? "select-readOnly" : ""}`}
        >
          {listOptions?.map((item: TypeCommon) => (
            <Select.Option
              disabled={item?.disabled}
              key={item?.value}
              value={item?.value}
            >
              {item?.label}
            </Select.Option>
          ))}
        </Select>
      ) : (
        <Select
          allowClear={allowClear}
          showSearch
          id={name}
          onDropdownVisibleChange={(open: TypeCommon) => {
            setIsDropdownVisible(open);
            if (!open) {
              isChanged.current = false;
            }
          }}
          onSelect={() => {
            isChanged.current = true;
          }}
          optionFilterProp="children"
          filterOption={(input, option): TypeCommon => {
            const value: TypeCommon = option?.children;
            return value?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
          }}
          disabled={disabled}
          className={`${readOnly ? "select-readOnly" : ""}`}
        >
          {listOptions?.map((item: TypeCommon) => (
            <Select.Option key={item?.value} value={item?.value}>
              {item?.label}
            </Select.Option>
          ))}
        </Select>
      )}
    </Form.Item>
  );
};

export default BaseSelect;
